import { useMemo, useState } from 'react'
import { motion, useReducedMotion } from 'motion/react'
import TarjetaProducto from '../components/TarjetaProducto'
import { useProductos } from '../hooks/useProductos'
import { usePedidoWhatsapp } from '../hooks/usePedidoWhatsapp'

// Umbral sugerido: por debajo de esto el filtro estorba más de lo que ayuda.
// La cantidad definitiva de productos está pendiente de confirmar con el
// cliente (rango estimado 5-15).
const UMBRAL_FILTRO = 8

const TODAS = 'todas'

// Los 5 productos reales de antes de esta funcionalidad no tienen `origen`
// guardado en Firestore: se tratan como PROPIO en vez de excluirlos de la
// pestaña principal, para no vaciar el catálogo el día que se despliega esto.
const PROPIO = 'propio'
const ALIANZA = 'alianza'
const origenDe = (producto) => producto.origen ?? PROPIO

function Esqueleto() {
  return (
    <div className="animate-pulse rounded-sm border border-oro-profundo/20 bg-carbon">
      <div className="aspect-[4/5] bg-leopardo/40" />
      <div className="space-y-3 p-4 sm:p-5">
        <div className="h-4 w-3/4 rounded-sm bg-leopardo/40" />
        <div className="h-3 w-full rounded-sm bg-leopardo/30" />
        <div className="h-5 w-1/3 rounded-sm bg-leopardo/40" />
      </div>
    </div>
  )
}

function Catalogo() {
  const { productos, cargando, error } = useProductos()
  const { pedir, pendiente, error: errorPedido } = usePedidoWhatsapp()
  const [categoria, setCategoria] = useState(TODAS)
  const [pestana, setPestana] = useState(PROPIO)
  const sinMovimiento = useReducedMotion()

  // La pestaña "Gatos de alianzas" solo se muestra si de verdad hay al menos
  // uno: mientras nadie cargue un producto con origen "alianza" en el admin,
  // el catálogo se ve exactamente igual que antes de esta funcionalidad.
  const hayAlianzas = useMemo(() => productos.some((p) => origenDe(p) === ALIANZA), [productos])

  const productosPestana = useMemo(
    () => productos.filter((p) => origenDe(p) === pestana),
    [productos, pestana]
  )

  const categorias = useMemo(() => {
    const unicas = new Set(productosPestana.map((p) => p.categoria).filter(Boolean))
    return [TODAS, ...[...unicas].sort((a, b) => a.localeCompare(b, 'es'))]
  }, [productosPestana])

  const mostrarFiltro = productosPestana.length > UMBRAL_FILTRO && categorias.length > 2

  const visibles = useMemo(
    () =>
      categoria === TODAS
        ? productosPestana
        : productosPestana.filter((p) => p.categoria === categoria),
    [productosPestana, categoria]
  )

  // Cambiar de pestaña resetea el filtro de raza: las razas de un catálogo no
  // tienen por qué existir en el otro, y arrastrar la selección podría dejar
  // el filtro activo sin ningún resultado.
  const cambiarPestana = (siguiente) => {
    setPestana(siguiente)
    setCategoria(TODAS)
  }

  return (
    <section
      id="catalogo"
      aria-labelledby="titulo-catalogo"
      className="seccion-y"
    >
      <div className="contenedor">
        <header className="max-w-2xl">
          <p className="text-[0.7rem] uppercase tracking-[0.3em] text-oro sm:text-xs sm:tracking-[0.35em]">
            Disponibles
          </p>
          <h2 id="titulo-catalogo" className="mt-4 text-seccion font-light">
            Gatitos disponibles
          </h2>
          <p className="mt-4 text-entradilla text-marfil-suave/80">
            Cada cachorro se entrega desparasitado, con su esquema de
            vacunación al día y evaluado por nuestro veterinario.
          </p>
        </header>

        {hayAlianzas && (
          <div
            role="tablist"
            aria-label="Catálogo"
            className="mt-8 flex gap-2 border-b border-oro-profundo/20 sm:mt-10"
          >
            {[
              { id: PROPIO, etiqueta: 'Nuestros gatos' },
              { id: ALIANZA, etiqueta: 'Gatos de alianzas' },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={pestana === tab.id}
                onClick={() => cambiarPestana(tab.id)}
                className={[
                  'min-h-11 border-b-2 px-1 text-sm transition-colors',
                  pestana === tab.id
                    ? 'border-oro text-oro-claro'
                    : 'border-transparent text-marfil-suave hover:text-oro-claro',
                ].join(' ')}
              >
                {tab.etiqueta}
              </button>
            ))}
          </div>
        )}

        {mostrarFiltro && (
          <div
            role="group"
            aria-label="Filtrar por raza"
            className="mt-8 flex flex-wrap gap-2 sm:mt-10"
          >
            {categorias.map((cat) => {
              const activa = cat === categoria
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setCategoria(cat)}
                  aria-pressed={activa}
                  className={[
                    'inline-flex min-h-10 items-center rounded-sm border px-4 py-2 text-xs uppercase tracking-[0.15em] transition-colors',
                    activa
                      ? 'border-oro bg-oro text-noche'
                      : 'border-oro-profundo/40 text-marfil-suave hover:border-oro-profundo hover:text-oro-claro',
                  ].join(' ')}
                >
                  {cat}
                </button>
              )
            })}
          </div>
        )}

        {errorPedido && (
          <p
            role="alert"
            className="mt-8 rounded-sm border border-oro-profundo bg-leopardo px-4 py-3 text-sm text-marfil"
          >
            {errorPedido}
          </p>
        )}

        {error ? (
          <p
            role="alert"
            className="mt-10 rounded-sm border border-oro-profundo bg-leopardo px-4 py-3 text-sm text-marfil"
          >
            {error}
          </p>
        ) : cargando ? (
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3 lg:gap-8">
            {Array.from({ length: 6 }, (_, i) => (
              <Esqueleto key={i} />
            ))}
          </div>
        ) : visibles.length === 0 ? (
          <p className="mt-10 text-sm text-marfil-suave">
            No hay gaticos disponibles en esta raza por ahora.
          </p>
        ) : (
          <ul className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3 lg:gap-8">
            {visibles.map((producto, i) => (
              <motion.li
                key={producto.id}
                layout={!sinMovimiento}
                initial={sinMovimiento ? false : { opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{
                  duration: 0.5,
                  delay: Math.min(i, 5) * 0.06,
                  ease: 'easeOut',
                }}
              >
                <TarjetaProducto
                  producto={producto}
                  onPedir={pedir}
                  pidiendo={pendiente === producto.id}
                  // Solo la primera tarjeta tiene chance real de estar sobre
                  // el pliegue (el grid es de una sola columna en móvil, y el
                  // hero ya ocupa ~85svh arriba). Marcar las siguientes como
                  // eager+high competía por ancho de banda contra el propio
                  // LCP sin necesidad — Lighthouse lo señaló explícitamente.
                  prioridad={i === 0}
                />
              </motion.li>
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}

export default Catalogo
